/**
 * Newsletter Vue component
 * ------------------------------------------------------------------------------
 * Main template in sections/index__newsletter.liquid and snippets/footer__newsletter.liquid
 *
 *
 */

import axios from 'axios'

import { getNewsletterAPI } from '../utils/api'

export default {
  props: {
    collectionName: {
      type: String
    },
    productSku: {
      type: String
    }
  },

  data () {
    return {
      isSubmitting: false,
      isSuccessful: false,
      email: '',
      firstName: '',
      hasValidationErrors: false,
      errors: {
        empty: false,
        invalid: false,
        firstNameEmpty: false
      },
      successMessage: '',
      errorMessage: ''
    }
  },

  beforeMount () {
    if (navigator.userAgent.indexOf('iPhone') > -1) {
      document
        .querySelector('[name=viewport]')
        .setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1')
    }

    const url = new URL(window.location)
    if (url.searchParams.get('newsletter-subscribe')) {
      this.isSuccessful = true
    }
  },

  computed: {
    isError () {
      return Object.values(this.errors).some((error) => error) || this.errorMessage
    },

    isButtonDisabled () {
      return this.isSubmitting || this.hasValidationErrors
    }
  },

  watch: {
    email () {
      // Re-enable button when email changes after validation errors
      if (this.hasValidationErrors) {
        this.hasValidationErrors = false
        this.resetErrors()
      }
    },

    firstName () {
      // Re-enable button when firstName changes after validation errors
      if (this.hasValidationErrors) {
        this.hasValidationErrors = false
        this.resetErrors()
      }
    }
  },

  methods: {
    hasFirstNameField () {
      // Check if firstName input field exists in the form (footer, PLP no products, PDP sold out)
      return !!this.$el.querySelector('input[name="inp_1"]')
    },

    async handleSubmitForm (e) {
      e.preventDefault()

      // Reset errors
      this.resetErrors()

      // Track if there are any validation errors
      let hasErrors = false

      // Check if firstName is empty (only if firstName field exists in the form)
      if (this.hasFirstNameField()) {
        if (!this.firstName || !this.firstName.trim()) {
          this.setError('firstNameEmpty')
          hasErrors = true
        }
      }

      // Check if email is empty
      if (!this.email) {
        this.setError('empty')
        hasErrors = true
      } else if (!this.validateEmail(this.email)) {
        // Check email format only if email is not empty
        this.setError('invalid')
        hasErrors = true
      }

      // If there are validation errors, don't submit
      if (hasErrors) {
        this.hasValidationErrors = true
        return
      }

      // Reset validation errors flag if form is valid
      this.hasValidationErrors = false

      this.isSubmitting = true
      const requestBody = {
        email: this.email
      }

      // Only include firstName if it's provided and the field exists
      if (this.hasFirstNameField() && this.firstName && this.firstName.trim()) {
        requestBody.first_name = this.firstName.trim()
      }

      if (this.productSku || this.collectionName) {
        requestBody.campaign = this.productSku || this.collectionName
      }

      const postCode = new URL(window.location).searchParams.get('postCode')
      if (postCode) {
        requestBody.post_code = postCode
      }

      if (window.Shopify.country !== 'AU') {
        requestBody.country_code = window.Shopify.country
      }

      axios.post(getNewsletterAPI(), requestBody)
        .then(({ data }) => {
          this.isSuccessful = true
          this.successMessage = data.message

          const url = new URL(window.location)
          url.searchParams.set('newsletter-subscribe', true)
          window.history.pushState({}, '', url)
        })
        .catch(({ response }) => {
          const { status, data } = response

          if (status < 500) {
            this.errorMessage = data.error
          } else {
            this.errorMessage = 'Server error. Please try again later.'
          }
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },

    validateEmail (fieldValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(fieldValue)
    },

    clearField () {
      this.email = ''
      this.firstName = ''
      this.resetErrors()
    },

    clearFirstName () {
      this.firstName = ''
      this.resetErrors()
    },

    clearEmail () {
      this.email = ''
      this.resetErrors()
    },

    setError (key) {
      this.errors[key] = true
    },

    resetErrors () {
      const errorKeys = Object.keys(this.errors)

      for (const key of errorKeys) {
        this.errors[key] = false
      }

      this.errorMessage = ''
      this.successMessage = ''
      this.hasValidationErrors = false
    }
  }
}
